<script setup lang="ts">
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
} from 'radix-vue';
import { getLocaleFromLocaleCode } from '@pkg/config/languages';
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import { CmsDepth } from '@pkg/config/misc/cmsDepth';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { Market } from '@pkg/config/languages/types';
import { useIsDesktop } from '@pkg/ui/composables/useIsDesktop';
import { cn } from '@pkg/ui/lib/utils';
import { pathPartsToSlugParts, urlToPathParts } from '@pkg/sanity-utils/urlToSlugParts';
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { HeaderThemeState } from '../types/headerTypes';
import { DEFAULT_HEADER_THEME_STATE } from '../composables/useHeaderThemeDetection';
import { useLocaleOptions } from '../composables/useLocaleOptions';
import { useSanitizedNavigationFromRoute } from '../composables/useSanitizedNavigationFromRoute';
import { languagePickerKey } from '../injectionKeys';
import ChevronRightIcon from './icons/ChevronRightIcon.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    contentSide?: 'top' | 'left' | 'right' | 'bottom';
    contentSideOffset?: number;
    contentClass?: string;
    backdrop?: boolean;
  }>(),
  { contentSide: 'bottom', contentSideOffset: 12, backdrop: false, contentClass: '' },
);

const isDesktop = useIsDesktop();
const injectedThemeState = inject<Ref<HeaderThemeState>>('headerThemeState');
const themeState = computed(() => injectedThemeState?.value ?? DEFAULT_HEADER_THEME_STATE);
const isDropdownOpen = ref(false);
const languagePickerOpen = inject<Ref<boolean>>('languagePickerOpen', ref(false));
const mainNavOpenValue = inject<Ref<string | undefined>>('mainNavOpenValue', ref(undefined));

/** Close dropdown when header opens another overlay (nav or autocomplete). */
watch(
  [mainNavOpenValue, () => languagePickerOpen.value],
  ([navVal, pickerOpen]) => {
    const shouldClose = navVal != null || !pickerOpen;
    if (shouldClose && isDropdownOpen.value) {
      isDropdownOpen.value = false;
    }
  },
);

const { options } = useLocaleOptions();
const languagePickerApi = inject(languagePickerKey, null);

watch(
  () => isDesktop.value && isDropdownOpen.value,
  (open) => {
    if (languagePickerOpen.value !== open) {
      languagePickerOpen.value = open;
    }
  },
  { immediate: true },
);

const { locale: localeCode } = useI18n();
const route = useRoute();
const { getSanitizedQueryAndHash } = useSanitizedNavigationFromRoute();
const localePath = useLocalePath();
const currentLocale = computed(() => getLocaleFromLocaleCode(localeCode.value as LOCALE_CODE));

const isActive = (country: Market, locale?: { languageName: string }) =>
  country.country === currentLocale.value?.country &&
  (!locale || locale.languageName === currentLocale.value?.languageName);

const showFlags = computed(() => !isDesktop.value);

function countryLabel(country: Market) {
  return showFlags.value ? `${country.icon} ${country.country}` : country.country;
}

/** Desktop: run on pointerdown (capture) so selection runs before Radix closes the dropdown. */
function selectLocale(locale: LOCALE_CODE, e?: PointerEvent) {
  e?.stopPropagation();
  onSelectLocale(locale);
}

/** Resolve the page's slug in the target locale (app-provided) and navigate; home when there is none. */
async function onSelectLocale(locale: LOCALE_CODE) {
  isDropdownOpen.value = false;
  if (localeCode.value === locale) {
    return;
  }

  // route.path is already locale-prefixed; urlToPathParts() handles stripping it.
  const pathParts = urlToPathParts(route.path);
  const slugParts = pathPartsToSlugParts(pathParts);

  const pathInLocale = (await languagePickerApi?.resolveLocaleSlug(locale, slugParts)) ?? '/';
  const to = localePath(pathInLocale, locale);

  const { query, hash } = getSanitizedQueryAndHash();
  await navigateTo({
    path: to,
    ...(Object.keys(query).length > 0 ? { query } : {}),
    ...(hash ? { hash } : {}),
  });
}

const resolvedContentClass = computed(() =>
  cn('language-picker__content', isDesktop.value ? 'language-picker__content--desktop' : 'language-picker__content--mobile', props.contentClass),
);

const listContainerStyle = computed(() => {
  if (!isDesktop.value) {
    return undefined;
  };

  return {
    color: themeState.value.textColor,
    '--theme-header-background-color': themeState.value.isLightTheme ? 'var(--theme-header-background-color-dark)' : 'var(--theme-header-background-color-light)',
    '--theme-header-text-color': themeState.value.isLightTheme ? 'var(--theme-header-text-color-dark)' : 'var(--theme-header-text-color-light)',
  };
});

onBeforeUnmount(() => {
  if (languagePickerOpen.value) {
    languagePickerOpen.value = false;
  }
});
</script>

<template>
  <div
    v-if="backdrop && isDropdownOpen"
    class="language-picker__backdrop"
    aria-hidden="true"
    :data-cms-depth="CmsDepth.LanguagePickerBackdrop"
    @click="isDropdownOpen = false"
  />
  <DropdownMenuRoot v-model:open="isDropdownOpen" :modal="!isDesktop">
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        v-bind="$attrs"
        :class="cn('language-picker__trigger', backdrop && 'language-picker__trigger--centered')"
        aria-label="Open language switcher menu"
        aria-haspopup="menu"
        :aria-expanded="isDropdownOpen"
      >
        {{ localeCode }}
      </button>
    </DropdownMenuTrigger>
    <!-- Desktop: list is teleported to header viewport (#header-viewport-content). -->
    <Teleport v-if="isDesktop && isDropdownOpen" to="#header-viewport-content">
      <div
        role="menu"
        aria-label="Language switcher"
        class="language-picker__list-wrapper"
        :style="listContainerStyle"
      >
        <div class="language-picker__list--desktop nav-content-slide-down">
          <template v-for="country in options" :key="country.country">
            <button
              v-if="country.locales.length === 1"
              type="button"
              role="menuitem"
              class="language-picker__item language-picker__item--country amp-auto-track"
              :data-analytics-click-behaviour="AnalyticsClickBehaviour.LanguagePicker"
              @pointerdown.capture.prevent="(e) => selectLocale(country.locales[0].localeCode, e)"
              @click.prevent="selectLocale(country.locales[0].localeCode)"
            >
              <span :class="cn('c-nav-item', isActive(country) && 'c-nav-item--selected')">
                {{ countryLabel(country) }}
                <ChevronRightIcon class="c-nav-item__chevron" />
              </span>
            </button>
            <template v-else>
              <div
                class="language-picker__label"
                role="presentation"
              >
                {{ countryLabel(country) }}
              </div>
              <button
                v-for="locale in country.locales"
                :key="locale.localeCode"
                type="button"
                role="menuitem"
                class="language-picker__item language-picker__item--locale amp-auto-track"
                :data-analytics-click-behaviour="AnalyticsClickBehaviour.LanguagePicker"
                @pointerdown.capture.prevent="(e) => selectLocale(locale.localeCode, e)"
                @click.prevent="selectLocale(locale.localeCode)"
              >
                <span :class="cn('c-nav-item', isActive(country, locale) && 'c-nav-item--selected')">
                  {{ locale.languageName }}
                  <ChevronRightIcon class="c-nav-item__chevron" />
                </span>
              </button>
            </template>
          </template>
        </div>
      </div>
    </Teleport>
    <DropdownMenuPortal>
      <DropdownMenuContent
        :side="contentSide"
        :side-offset="contentSideOffset"
        :class="resolvedContentClass"
        :style="isDesktop ? { position: 'fixed', inset: 0, pointerEvents: 'none', visibility: 'hidden', opacity: 0 } : undefined"
        :data-theme="isDesktop ? undefined : 'light'"
      >
        <div
          :class="{ 'language-picker__viewport-area': isDesktop }"
          class="language-picker__content-anim"
          :style="!isDesktop ? listContainerStyle : undefined"
        >
          <div
            :class="cn(
              isDesktop ? 'language-picker__list--desktop nav-content-slide-down' : 'language-picker__list--mobile',
            )"
          >
            <template v-if="!isDesktop">
              <template v-for="country in options" :key="country.country">
                <DropdownMenuItem
                  v-if="country.locales.length === 1"
                  class="language-picker__item language-picker__item--country"
                  @select="onSelectLocale(country.locales[0].localeCode)"
                >
                  <span :class="cn('c-nav-item', isActive(country) && 'c-nav-item--selected')">
                    {{ countryLabel(country) }}
                    <ChevronRightIcon class="c-nav-item__chevron" />
                  </span>
                </DropdownMenuItem>
                <template v-else>
                  <div
                    class="language-picker__label"
                    role="presentation"
                  >
                    {{ countryLabel(country) }}
                  </div>
                  <DropdownMenuItem
                    v-for="locale in country.locales"
                    :key="locale.localeCode"
                    class="language-picker__item language-picker__item--locale"
                    @select="onSelectLocale(locale.localeCode)"
                  >
                    <span :class="cn('c-nav-item', isActive(country, locale) && 'c-nav-item--selected')">
                      {{ locale.languageName }}
                      <ChevronRightIcon class="c-nav-item__chevron" />
                    </span>
                  </DropdownMenuItem>
                </template>
              </template>
            </template>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<style scoped>
.language-picker {
  &__backdrop {
    position: fixed;
    inset: 0;
    z-index: 50;
  }
  &__trigger {
    font-family: theme('fontFamily.body');
    font-size: theme('fontSize.14');
    display: flex;
    align-items: center;
    width: 100%;
    text-transform: uppercase;
    font-weight: theme('fontWeight.medium');
  }
  &__trigger--centered { justify-content: center; }
  &__label {
    font-family: theme('fontFamily.body');
    font-size: theme('fontSize.14');
    font-weight: theme('fontWeight.bold');
    text-transform: capitalize;
    cursor: default;
    padding: theme('spacing.8') 0;
    color: inherit;
  }
  &__list {
    &--mobile {
      display: flex; flex-direction: column;
      max-height: 180px;
      overflow: auto;
      padding: theme('spacing.4') theme('spacing.12') theme('spacing.12') theme('spacing.12');
      color: var(--theme-heading-color);
    }
    &--desktop {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      max-height: 53vh;
      align-content: flex-start;
      padding: theme('spacing.24') theme('spacing.40');
      color: inherit;
    }
  }
  &__item {
    font-family: theme('fontFamily.body');
    font-size: theme('fontSize.14');
    font-weight: theme('fontWeight.bold');
    text-transform: capitalize;
    cursor: pointer;
    outline: none;
    width: 20rem;
    text-align: left;
    background: none;
    border: none;
    color: inherit;
    padding: 0;
  }
  &__item--country { padding: theme('spacing.12') 0; }
  &__item--locale { font-weight: normal; padding: theme('spacing.12') 0; }
}
:deep(.language-picker__content) {
  user-select: none;
  min-width: 179px;
  width: 350px;
  border-radius: 0.5rem;
  border: none;
  z-index: 50;
}
.language-picker__content-anim {
  width: 100%;
}
:deep(.language-picker__content--mobile) {
  background-color: theme('colors.white');
}
:deep(.language-picker__content--desktop) {
  width: 100%;
  border-radius: 0;
  /* Override Radix inline transform so content stays full-width under the bar */
  transform: none !important;
}

.language-picker__viewport-area {
  /* Inner wrapper with fixed positioning so it stays full-width under header regardless of Radix */
  box-sizing: border-box;
}

.language-picker__list-wrapper {
  width: 100%;
  box-sizing: border-box;
}
:deep(.language-picker__content.nav-mobile__language-picker-content .language-picker__list--mobile) {
  max-height: 50vh;
}
</style>

<!-- Unscoped: Radix portal content is outside component DOM; pointer-events so clicks reach teleported list -->
<style>
.language-picker__content--desktop,
.language-picker__content--desktop * {
  pointer-events: none !important;
}
</style>
